<script>
import Icon from 'next/icon/Icon.vue';

const TOAST_VARIANTS = {
  success: {
    icon: 'i-lucide-circle-check',
    iconClass: 'text-n-teal-10 bg-n-teal-3',
    progressClass: 'bg-n-teal-9',
  },
  positive: {
    icon: 'i-lucide-circle-check',
    iconClass: 'text-n-teal-10 bg-n-teal-3',
    progressClass: 'bg-n-teal-9',
  },
  error: {
    icon: 'i-lucide-circle-alert',
    iconClass: 'text-n-ruby-10 bg-n-ruby-3',
    progressClass: 'bg-n-ruby-9',
  },
  alert: {
    icon: 'i-lucide-circle-alert',
    iconClass: 'text-n-ruby-10 bg-n-ruby-3',
    progressClass: 'bg-n-ruby-9',
  },
  warning: {
    icon: 'i-lucide-triangle-alert',
    iconClass: 'text-n-amber-10 bg-n-amber-3',
    progressClass: 'bg-n-amber-9',
  },
  info: {
    icon: 'i-lucide-info',
    iconClass: 'text-n-blue-10 bg-n-blue-3',
    progressClass: 'bg-n-blue-9',
  },
};

export default {
  components: { Icon },
  props: {
    message: { type: String, default: '' },
    action: {
      type: Object,
      default: () => {},
    },
    duration: {
      type: Number,
      default: 2500,
    },
    paused: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['dismiss'],
  data() {
    return {
      dismissTimerId: null,
      dismissStartedAt: null,
      dismissRemaining: this.duration,
      isPaused: false,
    };
  },
  computed: {
    isActionPresent() {
      return this.action && this.action.message;
    },
    variant() {
      return this.action?.variant || 'info';
    },
    toastStyle() {
      return TOAST_VARIANTS[this.variant] || TOAST_VARIANTS.info;
    },
    progressStyle() {
      return {
        '--toast-duration': `${this.duration}ms`,
        'animation-play-state': this.isPaused ? 'paused' : 'running',
      };
    },
  },
  watch: {
    paused(value) {
      if (value) {
        this.pauseDismiss();
      } else {
        this.resumeDismiss();
      }
    },
  },
  mounted() {
    this.startDismissTimer();
    if (this.paused) this.pauseDismiss();
  },
  beforeUnmount() {
    this.clearDismissTimer();
  },
  methods: {
    clearDismissTimer() {
      if (this.dismissTimerId) {
        window.clearTimeout(this.dismissTimerId);
        this.dismissTimerId = null;
      }
    },
    startDismissTimer() {
      this.clearDismissTimer();
      if (!this.dismissRemaining || this.dismissRemaining <= 0) return;

      this.dismissStartedAt = Date.now();
      this.dismissTimerId = window.setTimeout(() => {
        this.dismissTimerId = null;
        this.$emit('dismiss');
      }, this.dismissRemaining);
    },
    pauseDismiss() {
      // Prazos reais (como desfazer uma exclusão) não podem congelar no hover.
      if (this.action?.pauseOnHover === false) return;
      if (this.isPaused) return;
      this.isPaused = true;

      if (!this.dismissTimerId) return;

      const elapsed = Date.now() - this.dismissStartedAt;
      this.dismissRemaining = Math.max(0, this.dismissRemaining - elapsed);
      this.clearDismissTimer();
    },
    runAction() {
      this.action?.onClick?.();
      this.$emit('dismiss');
    },
    resumeDismiss() {
      if (!this.isPaused) return;
      this.isPaused = false;
      this.startDismissTimer();
    },
  },
};
</script>

<template>
  <div
    class="snackbar-card group pointer-events-auto relative mb-3 flex min-h-[4rem] w-full items-start gap-3 overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3 text-left shadow-lg ring-1 ring-n-slate-6/40 dark:bg-n-solid-2"
    role="alert"
    @mouseenter="pauseDismiss"
    @mouseleave="resumeDismiss"
    @focusin="pauseDismiss"
    @focusout="resumeDismiss"
  >
    <div
      class="mt-0.5 grid size-8 shrink-0 place-content-center rounded-full"
      :class="toastStyle.iconClass"
    >
      <Icon :icon="toastStyle.icon" class="size-4" />
    </div>

    <div class="min-w-0 flex-1">
      <div class="text-sm font-medium leading-5 text-n-slate-12">
        {{ message }}
      </div>
      <div v-if="isActionPresent" class="mt-1">
        <router-link
          v-if="action.type == 'link'"
          :to="action.to"
          class="text-sm font-medium text-n-blue-10 hover:text-n-blue-11"
        >
          {{ action.message }}
        </router-link>
        <div
          v-else-if="action.type == 'button'"
          class="flex flex-wrap items-center gap-2"
        >
          <button
            type="button"
            class="rounded-md border border-n-weak bg-n-alpha-2 px-2.5 py-1 text-sm font-semibold text-n-slate-12 transition hover:bg-n-alpha-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            @click="runAction"
          >
            {{ action.message }}
          </button>
          <span v-if="action.hint" class="text-xs text-n-slate-11">
            {{ action.hint }}
          </span>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="-mr-1 grid size-7 shrink-0 place-content-center rounded-md text-n-slate-10 opacity-70 transition hover:bg-n-alpha-2 hover:text-n-slate-12 group-hover:opacity-100"
      @click="$emit('dismiss')"
    >
      <Icon icon="i-lucide-x" class="size-4" />
    </button>

    <span
      class="toast-progress absolute bottom-0 left-0 h-1 rounded-br-full"
      :class="toastStyle.progressClass"
      :style="progressStyle"
    />
  </div>
</template>

<style scoped>
.toast-progress {
  animation: toast-progress-shrink var(--toast-duration) linear forwards;
}

@keyframes toast-progress-shrink {
  from {
    width: 100%;
  }

  to {
    width: 0%;
  }
}
</style>
